/**
 * Route-Component Mapping System
 *
 * This file provides a centralized mapping between routes, pages, and components
 * with clear company vs prime distinctions for maintainability and traceability.
 */

// ============================================================================
// ROUTE DEFINITIONS WITH METADATA
// ============================================================================

export interface RouteDefinition {
  path: string;
  component: string;
  page: string;
  domain: 'company' | 'prime' | 'shared' | 'admin';
  description: string;
  parent?: string;
  children?: RouteDefinition[];
  breadcrumb?: string;
  requiresAuth?: boolean;
  /** When true, only platform administrators may open the route (in addition to auth). */
  requiresPlatformAdmin?: boolean;
  /** When true, only a company manager or platform administrator may open the route. */
  requiresCompanyAdmin?: boolean;
  /** Omitted from production bundles' route table. */
  hideInProduction?: boolean;
}

export const ROUTE_MAPPINGS: Record<string, RouteDefinition> = {
  // ============================================================================
  // COMPANY ARTIFACTS (c-artifacts) - Actual Business Instances
  // ============================================================================

  'company-artifacts': {
    path: '/artifacts',
    component: 'CompanyArtifactsIndex',
    page: 'CompanyArtifactsIndexPage',
    domain: 'company',
    description: 'Redirect to company model tree selection',
    breadcrumb: 'Company Artifacts',
  },

  'company-artifacts-artifact-tree': {
    path: '/artifacts/artifact-tree',
    component: 'ArtifactTreeSelection',
    page: 'CompanyArtifactsSelectionPage',
    domain: 'company',
    description: 'Select company model from IntentML artifact-tree roots',
    parent: 'company-artifacts',
    breadcrumb: 'Select model',
  },

  'company-artifacts-select': {
    path: '/artifacts/select-tree',
    component: 'CompanyArtifactsSelectTreeRedirect',
    page: 'CompanyArtifactsSelectionRedirectPage',
    domain: 'company',
    description: 'Legacy redirect to /artifacts/artifact-tree',
    parent: 'company-artifacts',
    breadcrumb: 'Select Tree',
  },

  'company-artifacts-model': {
    path: '/artifacts/:modelSlug',
    component: 'CompanyArtifactLayout',
    page: 'CompanyArtifactsPage',
    domain: 'company',
    description: 'Company artifacts for a selected model workspace slug',
    parent: 'company-artifacts',
    breadcrumb: 'Company Artifacts',
  },

  'company-artifacts-create-root': {
    path: '/artifacts/create-root',
    component: 'NoRootPrompt',
    page: 'CompanyArtifactsCreateRootPage',
    domain: 'company',
    description: 'Create root company artifact',
    parent: 'company-artifacts',
    breadcrumb: 'Create Root',
  },

  'company-processes': {
    path: '/company-processes',
    component: 'CompanyProcessPage',
    page: 'CompanyProcessesPage',
    domain: 'company',
    description: 'Company processes and tasks management',
    breadcrumb: 'Company Processes',
  },

  'company-process-details': {
    path: '/company-processes/:processId',
    component: 'CompanyProcessPage',
    page: 'CompanyProcessDetailsPage',
    domain: 'company',
    description: 'Company process details and management',
    parent: 'company-processes',
    breadcrumb: 'Process Details',
  },

  'company-artifact-processes': {
    path: '/artifacts/:artifactId/processes',
    component: 'CompanyProcessPage',
    page: 'CompanyArtifactProcessesPage',
    domain: 'company',
    description: 'Company artifact processes management',
    parent: 'company-artifacts',
    breadcrumb: 'Artifact Processes',
  },

  // ============================================================================
  // PRIME ARTIFACTS (p-artifacts) - Templates/Models
  // ============================================================================

  'prime-artifacts': {
    path: '/prime/artifacts',
    component: 'PrimeModelLayout',
    page: 'PrimeArtifactsPage',
    domain: 'prime',
    description: 'Prime artifacts - templates and models',
    breadcrumb: 'Prime Artifacts',
  },

  'prime-artifacts-tracer': {
    path: '/prime/artifacts/tracer',
    component: 'ArtifactTransitionTracer',
    page: 'PrimeArtifactsTracerPage',
    domain: 'prime',
    description: 'Prime artifact transition tracer',
    parent: 'prime-artifacts',
    breadcrumb: 'Transition Tracer',
  },

  'prime-processes': {
    path: '/processes',
    component: 'ProcessPageManager',
    page: 'PrimeProcessesPage',
    domain: 'prime',
    description: 'Prime process relationships',
    breadcrumb: 'Process Relationships',
  },

  // ============================================================================
  // SHARED/ADMIN PAGES
  // ============================================================================

  'artifact-types': {
    path: '/artifact-types',
    component: 'ArtifactTypeList',
    page: 'ArtifactTypesPage',
    domain: 'admin',
    description: 'Artifact type definitions',
    breadcrumb: 'Artifact Types',
  },

  'parameter-types': {
    path: '/parameter-types',
    component: 'ParameterTypeList',
    page: 'ParameterTypesPage',
    domain: 'admin',
    description: 'Parameter type definitions',
    breadcrumb: 'Parameter Types',
  },

  'skills': {
    path: '/skills',
    component: 'SkillsPage',
    page: 'SkillsPage',
    domain: 'admin',
    description: 'Skill taxonomy management',
    breadcrumb: 'Skills',
  },

  'state-templates': {
    path: '/state-templates',
    component: 'StateTemplateLibraryManager',
    page: 'StateTemplatesPage',
    domain: 'admin',
    description: 'State template library management',
    breadcrumb: 'State Templates',
  },

  'option-catalogs': {
    path: '/option-catalogs',
    component: 'OptionCatalogManagementPage',
    page: 'OptionCatalogManagementPage',
    domain: 'admin',
    description: 'Option catalog management',
    breadcrumb: 'Option Catalogs',
  },

  'global-properties': {
    path: '/global-properties',
    component: 'GlobalPropertiesManagementPage',
    page: 'GlobalPropertiesManagementPage',
    domain: 'admin',
    description: 'Global properties management',
    breadcrumb: 'Global Properties',
  },

  'property-templates': {
    path: '/property-templates',
    component: 'GlobalPropertiesManagementPage',
    page: 'GlobalPropertiesManagementPage',
    domain: 'admin',
    description: 'Property templates management - manage property template definitions',
    breadcrumb: 'Property Templates',
    parent: 'global-properties',
  },

  'gpt-integration': {
    path: '/gpt-integration',
    component: 'GPTChat',
    page: 'GPTIntegrationPage',
    domain: 'admin',
    description: 'GPT integration and assistance',
    breadcrumb: 'GPT Assistant',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'architecture-agent': {
    path: '/architecture-agent',
    component: 'ArchitectureAgentPage',
    page: 'ArchitectureAgentPage',
    domain: 'admin',
    description: 'Architecture agent tools',
    breadcrumb: 'Architecture Agent',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  development: {
    path: '/development',
    component: 'DevelopmentPage',
    page: 'DevelopmentPage',
    domain: 'admin',
    description: 'Development tools and utilities',
    breadcrumb: 'Development',
    requiresAuth: true,
    requiresPlatformAdmin: true,
    hideInProduction: true,
  },

  'company-model-render-index': {
    path: '/render',
    component: 'CompanyModelRenderSurfacePage',
    page: 'CompanyModelRenderSurfacePage',
    domain: 'shared',
    description: 'Render surface for the header-selected company (kryspace slug derived)',
    breadcrumb: 'Render Surface',
    requiresAuth: true,
  },

  'company-model-render': {
    path: '/render/:workspaceSlug',
    component: 'CompanyModelRenderSurfacePage',
    page: 'CompanyModelRenderSurfacePage',
    domain: 'shared',
    description: 'Read-only kryspace company-model render surface (manifest consumer)',
    breadcrumb: 'Render Surface',
    requiresAuth: true,
  },

  'control-bridge-index': {
    path: '/render/control-bridge',
    component: 'ControlBridgePage',
    page: 'ControlBridgePage',
    domain: 'shared',
    description: 'Control Bridge surfaces for header-selected company (manifest-backed)',
    breadcrumb: 'Control Bridge',
    requiresAuth: true,
  },

  'control-bridge-slug': {
    path: '/render/:workspaceSlug/control-bridge',
    component: 'ControlBridgePage',
    page: 'ControlBridgePage',
    domain: 'shared',
    description: 'Control Bridge surfaces (manifest-backed, deep link by slug)',
    breadcrumb: 'Control Bridge',
    requiresAuth: true,
  },

  'unified-hub': {
    path: '/hub',
    component: 'UnifiedHubControlBridgePage',
    page: 'UnifiedHubControlBridgePage',
    domain: 'shared',
    description: 'Unified cross-domain Control Bridge hub (personal model manifest)',
    breadcrumb: 'Hub',
    requiresAuth: true,
  },

  'unified-hub-render-alias': {
    path: '/render/me/control-bridge',
    component: 'UnifiedHubControlBridgePage',
    page: 'UnifiedHubControlBridgePage',
    domain: 'shared',
    description: 'Unified hub alias deep link',
    breadcrumb: 'Hub',
    requiresAuth: true,
  },

  home: {
    path: '/',
    component: 'Home',
    page: 'HomePage',
    domain: 'shared',
    description: 'Application home page',
    breadcrumb: 'Home',
  },

  register: {
    path: '/register',
    component: 'RegisterPage',
    page: 'RegisterPage',
    domain: 'shared',
    description: 'Create account (email verification flow)',
    breadcrumb: 'Register',
    requiresAuth: false,
  },

  login: {
    path: '/login',
    component: 'LoginPage',
    page: 'LoginPage',
    domain: 'shared',
    description: 'Sign in with redirect (onboarding / deep links)',
    breadcrumb: 'Sign in',
    requiresAuth: false,
  },

  'forgot-password': {
    path: '/forgot-password',
    component: 'ForgotPasswordPage',
    page: 'ForgotPasswordPage',
    domain: 'shared',
    description: 'Request password reset',
    breadcrumb: 'Forgot password',
    requiresAuth: false,
  },

  'reset-password': {
    path: '/reset-password',
    component: 'ResetPasswordPage',
    page: 'ResetPasswordPage',
    domain: 'shared',
    description: 'Complete password reset with token',
    breadcrumb: 'Reset password',
    requiresAuth: false,
  },

  'onboarding-prospector': {
    path: '/onboarding/prospector',
    component: 'ProspectorChatPage',
    page: 'ProspectorChatPage',
    domain: 'shared',
    description: 'Intake — KRYBOT prospector chat (before Control Bridge)',
    breadcrumb: 'Discovery chat',
    requiresAuth: true,
  },

  'onboarding-paywall': {
    path: '/onboarding/paywall',
    component: 'OnboardingPaywallPage',
    page: 'OnboardingPaywallPage',
    domain: 'shared',
    description: 'Intake — Foundation Session / conversion gate (v1 placeholder)',
    breadcrumb: 'Foundation Session',
    requiresAuth: true,
  },

  'onboarding-confirmation': {
    path: '/onboarding/confirmation',
    component: 'FoundationConfirmationPage',
    page: 'FoundationConfirmationPage',
    domain: 'shared',
    description: 'Intake — post-booking confirmation',
    breadcrumb: 'Confirmation',
    requiresAuth: true,
  },

  'fitfinder-catalog': {
    path: '/fitfinder/catalog',
    component: 'FitfinderCatalogPage',
    page: 'FitfinderCatalogPage',
    domain: 'shared',
    description: 'FitFinder — ACTIVE solutions for workspace company',
    breadcrumb: 'FitFinder catalog',
    requiresAuth: true,
  },

  'fitfinder-session': {
    path: '/fitfinder/session/:sessionId',
    component: 'FitfinderSessionPage',
    page: 'FitfinderSessionPage',
    domain: 'shared',
    description: 'FitFinder — prospect session (Stripe return URL)',
    breadcrumb: 'FitFinder session',
    requiresAuth: true,
  },

  'verify-email': {
    path: '/verify-email',
    component: 'VerifyEmailPage',
    page: 'VerifyEmailPage',
    domain: 'shared',
    description: 'Confirm email address from verification link',
    breadcrumb: 'Verify email',
    requiresAuth: false,
  },

  'partner': {
    path: '/partner',
    component: 'PartnerHome',
    page: 'PartnerHomePage',
    domain: 'shared',
    description: 'Partner landing page',
    breadcrumb: 'Partner',
  },

  'resilience-circle-landing': {
    path: '/resilience-circle',
    component: 'ResilienceCircleLandingPage',
    page: 'ResilienceCircleLandingPage',
    domain: 'shared',
    description:
      'Resilience Circle lead-capture surface for the Five-Pillars assessment (ai-bsc-act-rc-016-design-lead-magnet)',
    breadcrumb: 'Resilience Circle',
    requiresAuth: false,
  },

  'keyed-landing': {
    path: '/go',
    component: 'KeyedLandingPage',
    page: 'KeyedLandingPage',
    domain: 'shared',
    description: 'Magnet / partner keyed landing (source_key → prospector)',
    breadcrumb: 'Start',
    requiresAuth: false,
  },

  'keyed-landing-magnet': {
    path: '/go/:magnetSlug',
    component: 'KeyedLandingPage',
    page: 'KeyedLandingPage',
    domain: 'shared',
    description: 'Keyed landing with magnet slug in path',
    breadcrumb: 'Start',
    requiresAuth: false,
    parent: 'keyed-landing',
  },

  resources: {
    path: '/resources',
    component: 'ResourcesLibraryPage',
    page: 'ResourcesLibraryPage',
    domain: 'shared',
    description: 'Resources library — articles and blogs',
    breadcrumb: 'Resources',
    requiresAuth: false,
  },

  'admin-resources': {
    path: '/admin/resources',
    component: 'ResourcesAdminPage',
    page: 'ResourcesAdminPage',
    domain: 'admin',
    description: 'Platform admin — author/upload Resources library content',
    breadcrumb: 'Resources admin',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'resources-article': {
    path: '/resources/:slug',
    component: 'ResourceArticlePage',
    page: 'ResourceArticlePage',
    domain: 'shared',
    description: 'Resource article detail (public or member-gated)',
    breadcrumb: 'Article',
    requiresAuth: false,
  },

  'partner-deferred-enroll': {
    path: '/partner/deferred-enroll',
    component: 'PartnerDeferredEnrollmentPage',
    page: 'PartnerDeferredEnrollmentPage',
    domain: 'shared',
    description: 'Partner deferred Core subscription enrollment',
    breadcrumb: 'Deferred enrollment',
    requiresAuth: true,
  },

  'chat-operational': {
    path: '/chat-operational',
    component: 'ChatOperationalLayout',
    page: 'ChatOperationalPage',
    domain: 'shared',
    description: 'Exo Panel - Human execution interface for artifact management and control',
    breadcrumb: 'Exo Panel',
    requiresAuth: true,
  },

  'company-admin-users': {
    path: '/company-admin/users',
    component: 'CompanyAdminUsersPage',
    page: 'CompanyAdminUsersPage',
    domain: 'company',
    description: 'Selected-company user roster and membership roles',
    breadcrumb: 'Company users',
    requiresAuth: true,
    requiresCompanyAdmin: true,
  },

  'company-admin-subscriptions': {
    path: '/company-admin/subscriptions',
    component: 'CompanyAdminSubscriptionsPage',
    page: 'CompanyAdminSubscriptionsPage',
    domain: 'company',
    description: 'Selected-company plans and seat assignment',
    breadcrumb: 'Company subscriptions',
    requiresAuth: true,
    requiresCompanyAdmin: true,
  },

  'company-admin-models': {
    path: '/company-admin/models',
    component: 'CompanyAdminModelsPage',
    page: 'CompanyAdminModelsPage',
    domain: 'company',
    description: 'Selected-company artifact trees',
    breadcrumb: 'Company models',
    requiresAuth: true,
    requiresCompanyAdmin: true,
  },

  'company-admin-billing': {
    path: '/company-admin/billing',
    component: 'CompanyAdminBillingPage',
    page: 'CompanyAdminBillingPage',
    domain: 'company',
    description: 'Selected-company billing account status',
    breadcrumb: 'Company billing',
    requiresAuth: true,
    requiresCompanyAdmin: true,
  },

  'company-admin-sessions': {
    path: '/company-admin/sessions',
    component: 'CompanyAdminSessionsPage',
    page: 'CompanyAdminSessionsPage',
    domain: 'company',
    description: 'Selected-company session bookings',
    breadcrumb: 'Company sessions',
    requiresAuth: true,
    requiresCompanyAdmin: true,
  },

  'user-management': {
    path: '/user-management',
    component: 'UserManagement',
    page: 'UserManagementPage',
    domain: 'admin',
    description: 'User management and administration',
    breadcrumb: 'User Management',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'company-management': {
    path: '/company-management',
    component: 'CompanyManagement',
    page: 'CompanyManagementPage',
    domain: 'admin',
    description: 'Company management and administration',
    breadcrumb: 'Company Management',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'company-detail': {
    path: '/company-management/:id',
    component: 'CompanyDetailPage',
    page: 'CompanyDetailPage',
    domain: 'admin',
    description: 'Company detail and management',
    breadcrumb: 'Company Detail',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'subscription-management': {
    path: '/subscription-management',
    component: 'SubscriptionManagement',
    page: 'SubscriptionManagementPage',
    domain: 'admin',
    description: 'Subscription management and administration',
    breadcrumb: 'Subscription Management',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'platform-subscriptions-catalog': {
    path: '/platform/subscriptions-catalog',
    component: 'PlatformSubscriptionCatalogPage',
    page: 'PlatformSubscriptionCatalogPage',
    domain: 'admin',
    description: 'Read-only subscription catalog. Mutations are refused on this page.',
    breadcrumb: 'Subscription catalog',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'subscription-types-management': {
    path: '/subscription-types-management',
    component: 'SubscriptionTypesManagement',
    page: 'SubscriptionTypesManagementPage',
    domain: 'admin',
    description: 'Subscription types and features management',
    breadcrumb: 'Subscription Types & Features',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'company-instances': {
    path: '/company-instances',
    component: 'CompanyInstanceManagement',
    page: 'CompanyInstancesPage',
    domain: 'admin',
    description: 'User-company membership links',
    breadcrumb: 'User-company links',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'demo': {
    path: '/demo',
    component: 'DemoPage',
    page: 'DemoPage',
    domain: 'shared',
    description: 'Interactive demo experience',
    breadcrumb: 'Demo',
  },

  'krybot-dashboard': {
    path: '/krybot',
    component: 'KrybotDashboard',
    page: 'KrybotDashboardPage',
    domain: 'admin',
    description: 'Krybot agent management dashboard',
    breadcrumb: 'Krybot Dashboard',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'bridge-migration': {
    path: '/admin/bridge-migration',
    component: 'BridgeMigrationPage',
    page: 'BridgeMigrationPage',
    domain: 'admin',
    description: 'Bridge migration - match artifact types and manage production mapping',
    breadcrumb: 'Bridge Migration',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'artifact-tree-manager': {
    path: '/admin/artifact-tree-manager',
    component: 'ArtifactTreeManager',
    page: 'ArtifactTreeManagerPage',
    domain: 'admin',
    description: 'Artifact tree management and administration',
    breadcrumb: 'Artifact Tree Manager',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'global-variables': {
    path: '/admin/global-variables',
    component: 'GlobalVariableManagementPage',
    page: 'GlobalVariableManagementPage',
    domain: 'admin',
    description: 'Global variable management and administration',
    breadcrumb: 'Global Variables',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  'global-properties-admin': {
    path: '/admin/global-properties',
    component: 'GlobalPropertiesManagementPage',
    page: 'GlobalPropertiesManagementPage',
    domain: 'admin',
    description: 'Global properties management - view and manage global property definitions and relationships',
    breadcrumb: 'Global Properties',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },

  // ============================================================================
  // HIERARCHICAL DASHBOARD ROUTES
  // ============================================================================

  'dashboard-legacy-redirect': {
    path: '/dashboard',
    component: 'DashboardLegacyRedirect',
    page: 'DashboardLegacyRedirectPage',
    domain: 'company',
    description: 'Legacy /dashboard root → manifest-backed render Control Bridge',
    breadcrumb: 'Control Bridge',
    requiresAuth: true,
  },

  'dashboard': {
    path: '/dashboard/*',
    component: 'HierarchicalDashboard',
    page: 'HierarchicalDashboardPage',
    domain: 'company',
    description: 'Control Bridge with drill-down navigation through business, product, operations, stakeholders, and customers',
    breadcrumb: 'Control Bridge',
  },

  'objective-detail': {
    path: '/dashboard/objective/:objectiveId',
    component: 'ObjectivePage',
    page: 'ObjectivePage',
    domain: 'company',
    description: 'Objective details with plan, artifact items hierarchy, and Gantt chart',
    parent: 'dashboard',
    breadcrumb: 'Objective',
  },

  'bridge-session-report': {
    path: '/bridge-session/:sessionId/report',
    component: 'BridgeSessionReportPage',
    page: 'BridgeSessionReportPage',
    domain: 'company',
    description: 'Full customer report: entropy, friction, worksheets, bridge card, analysis (PDF export)',
    breadcrumb: 'Session report',
    parent: 'bridge-session',
  },

  'client-model-snapshot': {
    path: '/client/:companyId/snapshot',
    component: 'ClientModelClientPage',
    page: 'ClientModelClientPage',
    domain: 'company',
    description: 'Client model snapshot — executive report envelope + projection trajectory (ISSUE-221/222)',
    breadcrumb: 'Client snapshot',
    requiresAuth: true,
  },

  'client-model-priorities': {
    path: '/client/:companyId/priorities',
    component: 'ClientModelClientPage',
    page: 'ClientModelClientPage',
    domain: 'company',
    description: 'Client priorities placeholder (supply §10)',
    breadcrumb: 'Priorities',
    parent: 'client-model-snapshot',
    requiresAuth: true,
  },

  'client-model-history': {
    path: '/client/:companyId/history',
    component: 'ClientModelClientPage',
    page: 'ClientModelClientPage',
    domain: 'company',
    description: 'Client change history placeholder (supply §10)',
    breadcrumb: 'History',
    parent: 'client-model-snapshot',
    requiresAuth: true,
  },

  'bridge-session': {
    path: '/bridge-session/:sessionId?',
    component: 'BridgeSessionPage',
    page: 'BridgeSessionPage',
    domain: 'company',
    description: 'Bridge Session flow for entropy reduction',
    breadcrumb: 'Bridge Session',
  },

  'upcoming-sessions': {
    path: '/upcoming-sessions',
    component: 'UpcomingSessionsPage',
    page: 'UpcomingSessionsPage',
    domain: 'shared',
    description: 'User Bridge Session bookings with change-time',
    breadcrumb: 'My Sessions',
    requiresAuth: true,
  },

  'local-bridge-users': {
    path: '/local-bridge/users',
    component: 'LocalBridgeUsersPage',
    page: 'LocalBridgeUsersPage',
    domain: 'shared',
    description: 'Local Bridge: manage users in the local database',
    breadcrumb: 'Users',
  },

  'local-bridge-companies': {
    path: '/local-bridge/companies',
    component: 'LocalBridgeCompaniesPage',
    page: 'LocalBridgeCompaniesPage',
    domain: 'shared',
    description: 'Local Bridge: manage companies in the local database',
    breadcrumb: 'Companies',
  },

  'local-bridge-company-transactions': {
    path: '/local-bridge/companies/:companyId/transactions',
    component: 'LocalBridgeCompanyTransactionsPage',
    page: 'LocalBridgeCompanyTransactionsPage',
    domain: 'shared',
    description: 'Local Bridge: view and manage company transactions (Revenue, Expense, Sales)',
    breadcrumb: 'Transactions',
  },

  'local-bridge': {
    path: '/local-bridge',
    component: 'LocalBridgePage',
    page: 'LocalBridgePage',
    domain: 'shared',
    description: 'Local Bridge hub: session list, create session, execute or view Control Bridge / Bridge Card',
    breadcrumb: 'Local Bridge',
  },

  'local-bridge-business-objective': {
    path: '/local-bridge/business-objective',
    component: 'BusinessObjectivePage',
    page: 'BusinessObjectivePage',
    domain: 'shared',
    description: 'Business domain: collect objective and current state (legacy)',
    breadcrumb: 'Business Objective',
  },

  'local-bridge-business-objective-workshop': {
    path: '/local-bridge/business-objective-workshop',
    component: 'BusinessObjectiveWorkshopPage',
    page: 'BusinessObjectiveWorkshopPage',
    domain: 'shared',
    description: 'Business Objective Workshop: 5-phase flow (Stability → Risk → Replication → Financial → Commitment)',
    breadcrumb: 'Business Objective Workshop',
  },

  'local-bridge-marketfit': {
    path: '/local-bridge/marketfit',
    component: 'LocalBridgeMarketFitPage',
    page: 'LocalBridgeMarketFitPage',
    domain: 'shared',
    description: 'Local Bridge MarketFit: deals, products, situations for a session',
    breadcrumb: 'MarketFit',
  },

  'local-bridge-product-domain-bridge': {
    path: '/local-bridge/product-domain-bridge/:sessionId',
    component: 'ProductDomainBridgeSessionPage',
    page: 'ProductDomainBridgeSessionPage',
    domain: 'shared',
    description: 'Product Domain Bridge Session: questions, entropy heat map, Bridge Card',
    breadcrumb: 'Product Domain Bridge',
  },

  'marketfit-situation': {
    path: '/dashboard/product/:productId/marketfit/situation/:situationId',
    component: 'MarketFitSituationPage',
    page: 'MarketFitSituationPage',
    domain: 'company',
    description: 'MarketFit Situation Definition: form + agent for assets, needs, inputs, outputs',
    parent: 'dashboard',
    breadcrumb: 'Situation',
  },

  'marketfit-solution': {
    path: '/dashboard/product/:productId/marketfit/solution/:solutionId',
    component: 'MarketFitSolutionPage',
    page: 'MarketFitSolutionPage',
    domain: 'company',
    description: 'MarketFit Solution Definition: form + agent for inputs and benefits',
    parent: 'dashboard',
    breadcrumb: 'Solution',
  },

  'local-bridge-marketfit-guided': {
    path: '/local-bridge/marketfit/guided',
    component: 'MarketFitGuidedFlowPage',
    page: 'MarketFitGuidedFlowPage',
    domain: 'shared',
    description: 'MarketFit Guided Flow: state-machine prompt-to-schema session',
    breadcrumb: 'Guided Session',
  },

  'local-bridge-control-bridge': {
    path: '/local-bridge/control-bridge',
    component: 'ControlBridgeDomainSelectorPage',
    page: 'ControlBridgeDomainSelectorPage',
    domain: 'shared',
    description: 'Control Bridge domain selection (post-workshop): select domain and pay commitment toll',
    breadcrumb: 'Control Bridge',
  },

  'admin-bridge-session-schedule': {
    path: '/admin/bridge-session-schedule',
    component: 'BridgeSessionSchedulePage',
    page: 'BridgeSessionSchedulePage',
    domain: 'admin',
    description: 'Admin view of full Bridge Session schedule',
    breadcrumb: 'Session Schedule',
    requiresAuth: true,
    requiresPlatformAdmin: true,
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get route definition by path
 */
export const getRouteByPath = (path: string): RouteDefinition | undefined => {
  return Object.values(ROUTE_MAPPINGS).find((route) => route.path === path);
};

/**
 * Get route definition by key
 */
export const getRouteByKey = (key: string): RouteDefinition | undefined => {
  return ROUTE_MAPPINGS[key];
};

/**
 * Get all routes for a specific domain
 */
export const getRoutesByDomain = (
  domain: 'company' | 'prime' | 'shared' | 'admin'
): RouteDefinition[] => {
  return Object.values(ROUTE_MAPPINGS).filter((route) => route.domain === domain);
};

/**
 * Get breadcrumb trail for a route
 */
export const getBreadcrumbTrail = (routeKey: string): string[] => {
  const route = ROUTE_MAPPINGS[routeKey];
  if (!route) return [];

  const trail: string[] = [];
  let current: RouteDefinition | undefined = route;

  while (current) {
    if (current.breadcrumb) {
      trail.unshift(current.breadcrumb);
    }
    current = current.parent ? ROUTE_MAPPINGS[current.parent] : undefined;
  }

  return trail;
};

/**
 * Get all company artifact routes
 */
export const getCompanyArtifactRoutes = (): RouteDefinition[] => {
  return getRoutesByDomain('company');
};

/**
 * Get all prime artifact routes
 */
export const getPrimeArtifactRoutes = (): RouteDefinition[] => {
  return getRoutesByDomain('prime');
};

// ============================================================================
// ROUTE VALIDATION
// ============================================================================

/**
 * Validate that all routes have required properties
 */
export const validateRouteMappings = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  Object.entries(ROUTE_MAPPINGS).forEach(([key, route]) => {
    if (!route.path) errors.push(`Route ${key} missing path`);
    if (!route.component) errors.push(`Route ${key} missing component`);
    if (!route.page) errors.push(`Route ${key} missing page`);
    if (!route.domain) errors.push(`Route ${key} missing domain`);
    if (!route.description) errors.push(`Route ${key} missing description`);

    // Validate domain values
    if (!['company', 'prime', 'shared', 'admin'].includes(route.domain)) {
      errors.push(`Route ${key} has invalid domain: ${route.domain}`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
};
